import { Button, Input, Popconfirm, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useState } from "react";
import {
  useCreateStaffRole,
  useDeleteStaffRole,
  useGetStaffRoles,
  useUpdateStaffRole,
} from "~/services/common";
import { TStaffRole } from "~/types";
import { NumberInput } from "../components/number_input";

interface StaffTabProps {}

export const StaffTab: React.FC<StaffTabProps> = () => {
  const [newRole, setNewRole] = useState("");
  const [editingRows, setEditingRows] = useState<
    Record<string, Partial<TStaffRole>>
  >({});
  const {
    data: staffRoles,
    isLoading: loadingStaffRoles,
    isRefetching: loadingRefetch,
    refetch,
  } = useGetStaffRoles();
  const createMutation = useCreateStaffRole();
  const updateMutation = useUpdateStaffRole();
  const deleteMutation = useDeleteStaffRole();

  const handleRateChange = (
    id: string,
    field: keyof TStaffRole,
    value: number
  ) => {
    setEditingRows((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleSave = (record: TStaffRole) => {
    const changes = editingRows[record.id];
    if (!changes || Object.keys(changes).length === 0) return;

    updateMutation.mutate(
      {
        id: record.id,
        ...changes,
      },
      {
        onSuccess: () => {
          setEditingRows((prev) => {
            const newState = { ...prev };
            delete newState[record.id];
            return newState;
          });
          refetch();
        },
        onError: (error: any) => {
          console.error(`Failed to update staff role:`, error);
        },
      }
    );
  };

  const getFieldValue = (
    record: TStaffRole,
    field: keyof TStaffRole
  ): number => {
    const editedValue = editingRows[record.id]?.[field];
    if (editedValue !== undefined) return Number(editedValue);

    // Handle the field mapping since TStaffRole might not have these exact fields
    if (field === "baseHourlyCost")
      return Number((record as any).baseHourlyCost ?? 0);
    if (field === "hourlyRate") return Number((record as any).hourlyRate ?? 0);

    return 0;
  };

  const hasChanges = (id: string): boolean => {
    return editingRows[id] && Object.keys(editingRows[id]).length > 0;
  };

  const handleDelete = (id: string, name: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        refetch();
      },
      onError: (error: any) => {
        console.error(`Failed to delete ${name}:`, error);
      },
    });
  };

  const addNewPosition = () => {
    const body = {
      name: newRole.trim(),
      baseHourlyCost: 0,
      hourlyRate: 0,
    };
    createMutation.mutate(body, {
      onSuccess: () => {
        setNewRole("");
        refetch();
      },
      onError: (error: any) => {
        console.error("Failed to add new position:", error);
      },
    });
  };

  const columns: ColumnsType<TStaffRole> = [
    {
      title: "Position",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Base Hourly Cost",
      dataIndex: "baseHourlyCost",
      key: "baseHourlyCost",
      render: (_, record) => (
        <NumberInput
          value={getFieldValue(record, "baseHourlyCost")}
          prefix="$"
          onChange={(newValue) =>
            newValue &&
            handleRateChange(record.id, "baseHourlyCost", Number(newValue))
          }
        />
      ),
    },
    {
      title: "OT (1.5x)",
      dataIndex: "baseHourlyCost",
      key: "overtime",
      render: (_, record) => {
        const baseRate = getFieldValue(record, "baseHourlyCost");
        return (
          <div className="px-3 py-2 bg-gray-100 border rounded">
            ${(baseRate * 1.5).toFixed(2)}
          </div>
        );
      },
    },
    {
      title: "Rate (Client Charge)",
      dataIndex: "hourlyRate",
      key: "hourlyRate",
      render: (_, record) => (
        <NumberInput
          value={getFieldValue(record, "hourlyRate")}
          prefix="$"
          onChange={(newValue) =>
            newValue &&
            handleRateChange(record.id, "hourlyRate", Number(newValue))
          }
        />
      ),
    },
    {
      title: "",
      key: "actions",
      width: "15%",
      align: "center",
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            type="primary"
            size="small"
            loading={updateMutation.isPending}
            onClick={() => handleSave(record)}
            disabled={!hasChanges(record.id)}
          >
            Save
          </Button>
          <Popconfirm
            title="Delete Position"
            description={`Are you sure you want to delete "${record.name}"?`}
            onConfirm={() => handleDelete(record.id, record.name)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ loading: deleteMutation.isPending }}
          >
            <Button danger size="small">
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Staff Position Rates</h3>
        <p className="text-gray-500 mb-4">
          Configure employee costs and client billing rates
        </p>
      </div>

      <Table
        columns={columns}
        dataSource={staffRoles?.items}
        rowKey="id"
        pagination={false}
        className="bg-white"
        loading={
          loadingStaffRoles || loadingRefetch || deleteMutation.isPending
        }
      />

      <div className="p-4 border rounded-lg bg-gray-50">
        <h4 className="font-medium mb-3">Add New Position</h4>
        <div className="flex gap-4">
          <Input
            placeholder="Position title"
            className="flex-grow"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
          />
          <Button
            type="primary"
            loading={createMutation.isPending}
            disabled={!newRole.trim()}
            onClick={() => {
              addNewPosition();
            }}
          >
            Add Position
          </Button>
        </div>
      </div>
    </div>
  );
};
